/**
 * Admin CosmosDB Query page component.
 * Allows admin users to execute read-only CosmosDB queries against Global or Cluster databases.
 * Only visible to users with admin status.
 *
 * @module pages/AdminPage
 */

import { useState, useCallback } from 'react';
import { Database, Play, AlertTriangle, ChevronDown, Copy, Check } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardBody } from '@/components/ui';
import {
  adminService,
  type AdminQueryDatabaseTarget,
  type AdminQueryCosmosDbResponse,
} from '@/services/adminService';

/** Containers available per database target. */
const GLOBAL_CONTAINERS = [
  'Sandboxes',
  'DiskImages',
  'Snapshots',
  'Connections',
  'SandboxGroups',
  'Volumes',
  'VolumeContainers',
  'SandboxSpaces',
];

const CLUSTER_CONTAINERS = ['Sandboxes', 'DiskImages', 'Snapshots'];

/**
 * Admin CosmosDB Query page
 */
export function AdminPage() {
  const [target, setTarget] = useState<AdminQueryDatabaseTarget>('Global');
  const [clusterId, setClusterId] = useState('');
  const [containerName, setContainerName] = useState(GLOBAL_CONTAINERS[0]);
  const [query, setQuery] = useState('SELECT * FROM c');
  const [maxItems, setMaxItems] = useState(100);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminQueryCosmosDbResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const containers = target === 'Global' ? GLOBAL_CONTAINERS : CLUSTER_CONTAINERS;

  // Reset container when target changes
  const handleTargetChange = useCallback(
    (newTarget: AdminQueryDatabaseTarget) => {
      setTarget(newTarget);
      const newContainers = newTarget === 'Global' ? GLOBAL_CONTAINERS : CLUSTER_CONTAINERS;
      if (!newContainers.includes(containerName)) {
        setContainerName(newContainers[0]);
      }
      setError(null);
    },
    [containerName]
  );

  const handleExecuteQuery = useCallback(async () => {
    setError(null);
    setResult(null);
    setIsLoading(true);

    try {
      const response = await adminService.queryCosmosDb({
        target,
        clusterId: target === 'Cluster' ? clusterId : undefined,
        containerName,
        query,
        maxItems,
      });
      setResult(response);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? (err as { detail?: string }).detail || err.message
          : 'An unexpected error occurred';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [target, clusterId, containerName, query, maxItems]);

  const handleCopyResults = useCallback(() => {
    if (!result) return;
    navigator.clipboard.writeText(JSON.stringify(result.results, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [result]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleExecuteQuery();
      }
    },
    [handleExecuteQuery]
  );

  return (
    <div className="p-6">
      <PageHeader
        title="Admin: CosmosDB Query"
        description="Execute read-only queries against CosmosDB databases"
      />

      {/* Warning banner */}
      <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Admin-only tool
            </h4>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
              Only read-only SELECT queries are allowed. This tool accesses production data &mdash;
              use with care.
            </p>
          </div>
        </div>
      </div>

      {/* Query form */}
      <Card className="mt-6">
        <CardBody>
          <div className="space-y-4">
            {/* Target + Container row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Target */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database Target
                </label>
                <div className="relative">
                  <select
                    value={target}
                    onChange={(e) => handleTargetChange(e.target.value as AdminQueryDatabaseTarget)}
                    className="w-full appearance-none rounded-lg border border-gray-300 dark:border-[#3a3a3a] bg-white dark:bg-[#1a1a1a] px-3 py-2 pr-10 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
                  >
                    <option value="Global">Global</option>
                    <option value="Cluster">Cluster</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Cluster ID (only shown for Cluster target) */}
              {target === 'Cluster' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cluster ID
                  </label>
                  <input
                    type="text"
                    value={clusterId}
                    onChange={(e) => setClusterId(e.target.value)}
                    placeholder="e.g. adc-eastus2-01"
                    className="w-full rounded-lg border border-gray-300 dark:border-[#3a3a3a] bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
                  />
                </div>
              )}

              {/* Container */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Container
                </label>
                <div className="relative">
                  <select
                    value={containerName}
                    onChange={(e) => setContainerName(e.target.value)}
                    className="w-full appearance-none rounded-lg border border-gray-300 dark:border-[#3a3a3a] bg-white dark:bg-[#1a1a1a] px-3 py-2 pr-10 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
                  >
                    {containers.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Max Items */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Items
                </label>
                <input
                  type="number"
                  value={maxItems}
                  onChange={(e) => setMaxItems(Math.max(1, Math.min(1000, Number(e.target.value))))}
                  min={1}
                  max={1000}
                  className="w-full rounded-lg border border-gray-300 dark:border-[#3a3a3a] bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
                />
              </div>
            </div>

            {/* Query input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                SQL Query
              </label>
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={5}
                placeholder="SELECT * FROM c WHERE c.id = 'xxx'"
                className="w-full rounded-lg border border-gray-300 dark:border-[#3a3a3a] bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm font-mono text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white resize-y"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Press Ctrl+Enter to execute
              </p>
            </div>

            {/* Execute button */}
            <div className="flex items-center gap-3">
              <Button onClick={handleExecuteQuery} disabled={isLoading || !query.trim()}>
                <Play className="h-4 w-4 mr-2" />
                {isLoading ? 'Executing...' : 'Execute Query'}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Error display */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <Card className="mt-6">
          <CardBody>
            {/* Results header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Results
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {result.count} row{result.count !== 1 ? 's' : ''} &middot;{' '}
                  {result.requestCharge.toFixed(2)} RU
                  {result.clusterId && <> &middot; Cluster: {result.clusterId}</>}
                </span>
              </div>
              <button
                onClick={handleCopyResults}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-[#3a3a3a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy JSON'}
              </button>
            </div>

            {/* Results table / JSON */}
            {result.count === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                No results found
              </p>
            ) : (
              <div className="overflow-auto max-h-[600px] rounded-lg border border-gray-200 dark:border-[#2a2a2a]">
                <pre className="p-4 text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                  {JSON.stringify(result.results, null, 2)}
                </pre>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
